import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { SearchIcon } from '@svgs';
import { businessQuery, locationsQuery } from '@queries';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

import { Location } from './Location/Location.jsx';

const matchLocation = (location, query) => {
  // determine filter match for a particular Location

  // explicit matching only
  const exactMatchFields = [
    location.id.toString(),
    location.partner_location_id,
  ];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const locationFields = [
    location.city,
    location.location_name,
    location.location_url,
    location.postal_code,
    location.state,
    location.state_name,
  ];
  // location name
  if (locationFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterLocations = (locations, query) => {
  // client side filtering of locations
  const cleanedQuery = query.toLowerCase().trim();
  return locations.filter((location) => {
    if (matchLocation(location, cleanedQuery)) {
      return location;
    }
  });
};

export const Locations = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // Fetches business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // Fetch sites
  const {
    data: locationsData,
    isPending: locationsIsPending,
    isError,
  } = useQuery(
    locationsQuery({
      business_id: businessID,
      limit: 1000,
      order_by: 'location_name',
      _fields: [
        'id',
        'location_name',
        'street',
        'street2',
        'city',
        'state',
        'postal_code',
        'state_name',
        'description',
        'service_area_only',
        'phonemap',
        'partner_location_id',
        'published',
      ].join(','),
    }),
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter locations if there is a search term, otherwise return the full list
  const filteredLocations = searchTerm
    ? filterLocations(locationsData.objects, searchTerm)
    : locationsData?.objects;

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <span>{t('Location details')}</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-websites">
            <h1>{t('Location details')}</h1>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left"></div>
              <div className="sb-manage-options-right">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={locationsIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
            </div>
            {locationsIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!locationsIsPending &&
              (isError || locationsData.objects?.length == 0) && (
                <WelcomeElement>
                  <p>No locations under this project</p>
                  {/**<Link to={`/dashboard/projects/new`} className="sb-btn sb-btn-save">Add first website</Link>**/}
                </WelcomeElement>
              )}
            {!locationsIsPending &&
              !isError &&
              locationsData.objects?.length >= 1 && (
                <div className="sb-content-view-row">
                  {filteredLocations.map((location) => {
                    return (
                      <Location
                        key={location.id}
                        location={location}
                        businessID={businessID}
                        businessData={businessData}
                      />
                    );
                  })}
                </div>
              )}
            {!locationsIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredLocations.length} of ${locationsData.objects.length} location` +
                  (locationsData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
